import axios from "axios";
import config from "../config";

// Set-password endpoint for a freshly invited shop.
//
// The backend (PasswordSetupLink) emails {POS_URL}/new-shop-password?token=…
// where the token is a Devise reset token. devise_token_auth is configured with
// require_client_password_reset_token = true, so PUT /shop_auth/password accepts
// that token directly in the body — no auth header, and no detour through
// /shop_auth/password/edit.
//
// NOTE: this is NOT the mechanism the customer web app uses on its
// /new-user-password page. That page talks to /customer/valid_token and
// /customer/update_password, which read the legacy plaintext
// users.new_password_token column and exist for User only. Shops have no such
// endpoint — there is no way to validate the token before submitting it.
//
// Dedicated axios instance, like publicInvoiceApi: the token in the URL IS the
// credential here. withCredentials: false matters twice over —
//   1. the till may be signed in as another shop; that session cookie must not
//      travel with this request, and
//   2. the response carries Set-Cookie + access-token headers for the shop whose
//      password was just set. On the global instance, authHelper's response
//      interceptor would store those and silently swap the till's session.
const client = axios.create({
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Thrown when the backend rejects the token itself (401) rather than the chosen
// password, so the page can show the dead-link screen instead of a form error.
export class InvalidTokenError extends Error {}

// devise_token_auth answers with `errors` as either an array of strings
// (unauthorized, missing password) or the record's error hash alongside
// full_messages (validation). Flatten both into one line.
const firstError = (data) => {
  const errors = data?.errors;
  if (Array.isArray(errors)) return errors[0];
  if (errors?.full_messages?.length) return errors.full_messages[0];
  return null;
};

export async function setShopPassword({ token, password, passwordConfirmation }) {
  try {
    const res = await client.put(`${config.ENDPOINT_AUTH}/password`, {
      reset_password_token: token,
      password,
      password_confirmation: passwordConfirmation,
    });
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) {
      throw new InvalidTokenError(firstError(err.response.data) || "Unauthorized");
    }
    const message = firstError(err?.response?.data);
    throw new Error(message || "NETWORK");
  }
}

export { firstError as __firstError };
